import { createRoute, redirect } from "@tanstack/react-router";
import { Add01Icon, Delete02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { api, ApiError, type DashUser } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { rootRoute } from "./root-route";

export const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  beforeLoad: async () => {
    try {
      await api.me();
    } catch {
      throw redirect({
        to: "/login",
        search: { returnTo: "/users", error: undefined },
      });
    }
  },
  component: UsersPage,
});

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function UsersTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-col gap-0">
        <div className="flex gap-4 border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-16" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-0"
          >
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    enabled: auth.status === "authed",
  });

  const createMutation = useMutation({
    mutationFn: (value: string) => api.createUser(value),
    onSuccess: async () => {
      setEmail("");
      setFormError(null);
      toast.add({
        title: "用户已添加",
        description: "对方可通过魔法链接登录。",
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "already_exists") {
        setFormError("该邮箱已在白名单中。");
      } else if (err instanceof ApiError && err.code === "forbidden") {
        setFormError("仅管理员可添加用户。");
      } else if (err instanceof ApiError && err.code === "valid email required") {
        setFormError("请输入有效的邮箱地址。");
      } else {
        setFormError("无法添加用户，请重试。");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => api.deleteUser(userId),
    onSuccess: async () => {
      toast.add({
        title: "用户已移除",
        description: "对方将无法再登录。",
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "";
      let description = "无法移除用户。";
      if (code === "cannot_remove_self") {
        description = "不能移除自己的账号。";
      } else if (code === "cannot_remove_last_admin") {
        description = "请至少保留一名管理员。";
      } else if (code === "forbidden") {
        description = "仅管理员可移除用户。";
      }
      toast.add({
        title: "移除失败",
        description,
        type: "error",
      });
    },
  });

  if (auth.status === "loading") {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-4 w-96" />
        </div>
        <UsersTableSkeleton />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  const canManage = usersQuery.data?.canManageUsers ?? auth.me.canManageUsers;
  const users = usersQuery.data?.users ?? [];
  const selfId = auth.me.userId;

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || createMutation.isPending) return;
    setFormError(null);
    createMutation.mutate(value);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          用户
        </h1>
        <p className="text-sm text-muted-foreground">
          登录白名单。仅列表中的邮箱可登录。仅管理员可添加或移除用户。
        </p>
      </div>

      {canManage ? (
        <form
          onSubmit={onAdd}
          className="rounded-lg border border-border p-4"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="user-email">添加邮箱</FieldLabel>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="user-email"
                  type="email"
                  autoComplete="off"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="sm:max-w-sm"
                />
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <Spinner />
                  ) : (
                    <HugeiconsIcon
                      icon={Add01Icon}
                      data-icon="inline-start"
                    />
                  )}
                  添加用户
                </Button>
              </div>
              <FieldDescription>
                任意域名均可。对方通过魔法链接登录。
              </FieldDescription>
            </Field>
          </FieldGroup>
          {formError ? (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">
          你可以查看白名单。如需添加或移除用户，请联系管理员。
        </p>
      )}

      {usersQuery.isLoading && <UsersTableSkeleton />}

      {usersQuery.isError && (
        <p className="text-sm text-destructive" role="alert">
          无法加载用户，请刷新重试。
        </p>
      )}

      {usersQuery.isSuccess && users.length === 0 && (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>暂无用户</EmptyTitle>
            <EmptyDescription>
              添加邮箱以允许魔法链接登录。
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {usersQuery.isSuccess && users.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>邮箱</TableHead>
                <TableHead>角色</TableHead>
                <TableHead>添加时间</TableHead>
                {canManage ? <TableHead className="w-28">操作</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  canManage={canManage}
                  isSelf={user.id === selfId}
                  busy={deleteMutation.isPending}
                  onRemove={() => deleteMutation.mutate(user.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  canManage,
  isSelf,
  busy,
  onRemove,
}: {
  user: DashUser;
  canManage: boolean;
  isSelf: boolean;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {user.email}
        {isSelf ? (
          <span className="ml-2 text-xs text-muted-foreground">你</span>
        ) : null}
      </TableCell>
      <TableCell>
        {user.canManageUsers ? (
          <Badge variant="secondary">管理员</Badge>
        ) : (
          <Badge variant="outline">成员</Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatWhen(user.createdAt)}
      </TableCell>
      {canManage ? (
        <TableCell>
          {isSelf ? (
            <span className="text-xs text-muted-foreground">-</span>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                  />
                }
              >
                <HugeiconsIcon icon={Delete02Icon} data-icon="inline-start" />
                移除
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>移除 {user.email}？</AlertDialogTitle>
                  <AlertDialogDescription>
                    对方将立即失去访问权限，重新添加前无法登录。
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>取消</AlertDialogCancel>
                  <AlertDialogAction onClick={onRemove} disabled={busy}>
                    移除用户
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
}
